/**
 * SME Portal — Playback Sessions page.
 * View scheduled playback sessions and client feedback.
 * SME/EM can schedule playback calls via Teams/Outlook calendar.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Typography, Box, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, CircularProgress, Alert, Button,
  Accordion, AccordionSummary, AccordionDetails, Tabs, Tab,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Stack,
  FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import { ExpandMore, CheckCircle, CalendarMonth, Edit, Cancel, EventRepeat, Settings, Delete, DeleteSweep } from '@mui/icons-material';
import { authFetch } from '../../../api/authFetch';
import RefreshButton from '../../../components/RefreshButton';
import UserPreferencesDialog from '../../../components/UserPreferencesDialog';

/** Common IANA timezone identifiers for the timezone dropdown. */
const COMMON_TIMEZONES = [
  'UTC',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Zurich',
  'Europe/Amsterdam',
  'Europe/Dublin',
  'Europe/Madrid',
  'Europe/Rome',
  'Europe/Stockholm',
  'Europe/Warsaw',
  'Europe/Moscow',
  'Europe/Istanbul',
  'Africa/Lagos',
  'Africa/Johannesburg',
  'Africa/Cairo',
  'Africa/Nairobi',
  'Asia/Dubai',
  'Asia/Riyadh',
  'Asia/Kolkata',
  'Asia/Singapore',
  'Asia/Hong_Kong',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Asia/Seoul',
  'Australia/Sydney',
  'Australia/Melbourne',
  'Pacific/Auckland',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Toronto',
  'America/Sao_Paulo',
];

interface PlaybackSessionItem {
  id: string;
  assessment_id: string;
  organization_id: string | null;
  scheduled_date: string | null;
  scheduled_end_date: string | null;
  scheduled_timezone: string | null;
  meeting_link: string | null;
  status: string;
  participants: string[] | null;
  notes: string | null;
  scheduled_by: string | null;
  created_at: string | null;
  assessment_domain: string | null;
  assessment_category: string | null;
  organization_name: string | null;
}

interface FeedbackItem {
  id: string;
  playback_session_id: string;
  assessment_report_id: string | null;
  feedback_text: string | null;
  feedback_items: any[] | null;
  status: string;
  created_at: string | null;
}

interface AssessmentOption {
  id: string;
  domain: string;
  category: string;
  organization_id: string | null;
  organization_name: string;
  report_status: string;
  has_active_playback: boolean;
  created_at: string | null;
}

interface PlaybackAssessmentRecord {
  id: string;
  organization_id: string | null;
  organization_name: string;
  report_status: string;
  has_active_playback: boolean;
  created_at: string | null;
}

interface PlaybackAssessmentType {
  name: string;
  domain_id: string | null;
  records: PlaybackAssessmentRecord[];
}

interface PlaybackDomainOption {
  name: string;
  assessments: PlaybackAssessmentType[];
}

/** Flatten the domain → assessment → record tree for lookups by assessment id. */
export function flattenPlaybackOptions(domains: PlaybackDomainOption[]): AssessmentOption[] {
  return domains.flatMap((d) => d.assessments.flatMap((a) => a.records.map((r) => ({
    ...r,
    domain: a.name,
    category: d.name,
  }))));
}

function describeRecord(r: { organization_name: string; created_at: string | null; report_status: string; has_active_playback: boolean }): string {
  const date = r.created_at ? new Date(r.created_at).toLocaleDateString('en-GB') : 'undated';
  const playback = r.has_active_playback ? ' · playback already scheduled' : '';
  return `${r.organization_name} — ${date} (${r.report_status})${playback}`;
}

/**
 * Extract HH:MM time string from a UTC ISO date in a given IANA timezone.
 * Uses Intl.DateTimeFormat so the result reflects the correct local time,
 * regardless of the browser's own timezone.
 */
function timeInZone(isoUtc: string, tz: string): string {
  const dt = new Date(isoUtc);
  if (isNaN(dt.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: tz,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(dt);
  const h = parts.find(p => p.type === 'hour')?.value ?? '00';
  const m = parts.find(p => p.type === 'minute')?.value ?? '00';
  return `${h}:${m}`;
}

/** Extract YYYY-MM-DD date string from a UTC ISO date in a given IANA timezone. */
function dateInZone(isoUtc: string, tz: string): string {
  const dt = new Date(isoUtc);
  if (isNaN(dt.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(dt);
  const y = parts.find(p => p.type === 'year')?.value ?? '2026';
  const mo = parts.find(p => p.type === 'month')?.value ?? '01';
  const d = parts.find(p => p.type === 'day')?.value ?? '01';
  return `${y}-${mo}-${d}`;
}

/** Format a UTC ISO date for display in a given IANA timezone (DD/MM/YYYY HH:MM). */
function displayInZone(isoUtc: string, tz: string): string {
  const dt = new Date(isoUtc);
  if (isNaN(dt.getTime())) return '—';
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: tz,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(dt);
}

const PlaybackFeedbackReview: React.FC = () => {
  const location = useLocation();
  const isEMPortal = location.pathname.startsWith('/em');
  const [sessions, setSessions] = useState<PlaybackSessionItem[]>([]);
  const [feedback, setFeedback] = useState<FeedbackItem[]>([]);
  const [assessments, setAssessments] = useState<AssessmentOption[]>([]);
  const [domainOptions, setDomainOptions] = useState<PlaybackDomainOption[]>([]);
  const [selectedDomain, setSelectedDomain] = useState('');
  const [selectedAssessmentType, setSelectedAssessmentType] = useState('');
  const [loading, setLoading] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [tabIndex, setTabIndex] = useState(0);

  // Schedule dialog state
  const [scheduleDialog, setScheduleDialog] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [scheduleEndTime, setScheduleEndTime] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [selectedAssessmentId, setSelectedAssessmentId] = useState('');
  const [meetingLink, setMeetingLink] = useState('');
  const [scheduleTimezone, setScheduleTimezone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  );
  const [scheduling, setScheduling] = useState(false);
  const [isAssessmentLocked, setIsAssessmentLocked] = useState(false);

  // Edit dialog state
  const [editDialog, setEditDialog] = useState(false);
  const [editingSession, setEditingSession] = useState<PlaybackSessionItem | null>(null);
  const [editDate, setEditDate] = useState('');
  const [editTime, setEditTime] = useState('');
  const [editEndTime, setEditEndTime] = useState('');
  const [editMeetingLink, setEditMeetingLink] = useState('');
  const [editClientEmail, setEditClientEmail] = useState('');
  const [editStatus, setEditStatus] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editTimezone, setEditTimezone] = useState('UTC');
  const [saving, setSaving] = useState(false);

  // Preferences dialog state
  const [prefsDialog, setPrefsDialog] = useState(false);

  const fetchData = useCallback(() => {
    setLoading(true);
    const optionsRequest = isEMPortal
      ? authFetch('/api/sme/playback-assessment-options').then((r) => r.json())
      : Promise.resolve({ success: false });
    Promise.all([
      authFetch('/api/sme/playback-sessions').then((r) => r.json()),
      authFetch('/api/sme/playback-feedback').then((r) => r.json()),
      optionsRequest,
    ])
      .then(([sessionsData, feedbackData, optionsData]) => {
        if (sessionsData.success) setSessions(sessionsData.sessions);
        if (feedbackData.success) setFeedback(feedbackData.feedback);
        if (optionsData.success && Array.isArray(optionsData.domains)) {
          setDomainOptions(optionsData.domains);
          setAssessments(flattenPlaybackOptions(optionsData.domains));
        }
      })
      .catch(() => setError('Failed to load playback data'))
      .finally(() => { setLastRefreshed(new Date()); setLoading(false); });
  }, [isEMPortal]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Check if navigated with assessment context (e.g., from "Schedule" button)
  useEffect(() => {
    const state = location.state as { assessmentId?: string } | null;
    if (state?.assessmentId) {
      setSelectedAssessmentId(state.assessmentId);
      setIsAssessmentLocked(true);
      setScheduleDialog(true);
      // Clear the state so browser back/forward doesn't re-trigger
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleIncorporate = async (reportId: string) => {
    try {
      const res = await authFetch(`/api/sme/reports/${reportId}/incorporate-feedback`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ review_notes: 'Feedback incorporated' }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        setError(errData.error || 'Failed to incorporate feedback');
        return;
      }
      setSuccess('Feedback marked as incorporated successfully.');
      fetchData();
    } catch {
      setError('Failed to incorporate feedback');
    }
  };

  const assessmentTypesForDomain = domainOptions.find((d) => d.name === selectedDomain)?.assessments ?? [];
  const recordsForAssessmentType = assessmentTypesForDomain.find((a) => a.name === selectedAssessmentType)?.records ?? [];

  const closeScheduleDialog = () => {
    setScheduleDialog(false);
    setIsAssessmentLocked(false);
    setSelectedAssessmentId('');
    setSelectedDomain('');
    setSelectedAssessmentType('');
  };

  const handleSchedulePlayback = async () => {
    if (!scheduleDate || !scheduleTime || !clientEmail || !selectedAssessmentId) return;
    setScheduling(true);
    try {
      const res = await authFetch('/api/sme/schedule-playback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date: scheduleDate,
          time: scheduleTime,
          end_time: scheduleEndTime || undefined,
          client_email: clientEmail,
          assessment_id: selectedAssessmentId,
          meeting_link: meetingLink,
          timezone: scheduleTimezone,
        }),
      });
      if (res.ok) {
        setSuccess('Playback session scheduled successfully.');
        setScheduleDialog(false);
        setScheduleDate('');
        setScheduleTime('');
        setScheduleEndTime('');
        setClientEmail('');
        setSelectedAssessmentId('');
        setSelectedDomain('');
        setSelectedAssessmentType('');
        setMeetingLink('');
        setIsAssessmentLocked(false);
        fetchData();
      } else {
        const errData = await res.json();
        setError(errData.error || 'Failed to schedule playback session.');
      }
    } catch {
      setError('Failed to schedule playback call');
    } finally {
      setScheduling(false);
    }
  };

  const openEditDialog = (session: PlaybackSessionItem) => {
    setEditingSession(session);
    const tz = session.scheduled_timezone || 'UTC';
    setEditDate(session.scheduled_date ? dateInZone(session.scheduled_date, tz) : '');
    setEditTime(session.scheduled_date ? timeInZone(session.scheduled_date, tz) : '');
    setEditEndTime(session.scheduled_end_date ? timeInZone(session.scheduled_end_date, tz) : '');
    setEditMeetingLink(session.meeting_link || '');
    setEditClientEmail(session.participants?.[0] || '');
    setEditStatus(session.status);
    setEditNotes(session.notes || '');
    setEditTimezone(tz);
    setEditDialog(true);
  };

  const handleSaveEdit = async () => {
    if (!editingSession) return;
    setSaving(true);
    try {
      const body: any = {
        status: editStatus,
        notes: editNotes,
        meeting_link: editMeetingLink,
      };
      if (editDate && editTime) {
        body.scheduled_date = `${editDate}T${editTime}:00`;
        body.timezone = editTimezone;
      }
      if (editDate && editEndTime) {
        body.scheduled_end_date = `${editDate}T${editEndTime}:00`;
      }
      if (editClientEmail) {
        const currentParticipants = editingSession.participants || [];
        body.participants = [editClientEmail, ...currentParticipants.slice(1)];
      }
      const res = await authFetch(`/api/sme/playback-sessions/${editingSession.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        setSuccess('Playback session updated.');
        setEditDialog(false);
        setEditingSession(null);
        fetchData();
      } else {
        const errData = await res.json();
        setError(errData.error || 'Failed to update session.');
      }
    } catch {
      setError('Failed to update playback session');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelSession = async (session: PlaybackSessionItem) => {
    if (!window.confirm('Are you sure you want to cancel this playback session?')) return;
    try {
      const res = await authFetch(`/api/sme/playback-sessions/${session.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'cancelled' }),
      });
      if (res.ok) {
        setSuccess('Playback session cancelled.');
        fetchData();
      } else {
        const errData = await res.json();
        setError(errData.error || 'Failed to cancel session.');
      }
    } catch {
      setError('Failed to cancel session');
    }
  };

  const handlePostponeSession = (session: PlaybackSessionItem) => {
    // Open the edit dialog pre-filled so the user can change the date/time
    openEditDialog(session);
    setEditStatus('scheduled'); // keep it scheduled, just reschedule
  };

  const handleDeleteSession = async (session: PlaybackSessionItem) => {
    if (!window.confirm('Delete this playback session? This action cannot be undone.')) return;
    try {
      const res = await authFetch(`/api/sme/playback-sessions/${session.id}`, {
        method: 'DELETE',
      });
      if (res.ok) {
        setSuccess('Playback session deleted.');
        fetchData();
      } else {
        const errData = await res.json();
        setError(errData.error || 'Failed to delete session.');
      }
    } catch {
      setError('Failed to delete session');
    }
  };

  const handleClearHistory = async () => {
    const count = sessions.filter(s => s.status === 'completed' || s.status === 'cancelled').length;
    if (count === 0) { setError('No completed or cancelled sessions to clear.'); return; }
    if (!window.confirm(`Clear ${count} completed/cancelled session(s)? This cannot be undone.`)) return;
    try {
      const res = await authFetch('/api/sme/playback-sessions/clear', { method: 'DELETE' });
      if (res.ok) {
        const data = await res.json();
        setSuccess(data.message || 'History cleared.');
        fetchData();
      } else {
        const errData = await res.json();
        setError(errData.error || 'Failed to clear history.');
      }
    } catch {
      setError('Failed to clear history');
    }
  };

  const getStatusColor = (status: string): 'info' | 'warning' | 'success' | 'error' | 'default' => {
    switch (status) {
      case 'scheduled': return 'info';
      case 'in_progress': return 'warning';
      case 'completed': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Client&apos;s Playback
        </Typography>
        <RefreshButton onRefresh={fetchData} loading={loading} lastRefreshed={lastRefreshed} />
        {isEMPortal && (
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<Settings />} onClick={() => setPrefsDialog(true)}>
              Timezone Settings
            </Button>
            <Button variant="contained" startIcon={<CalendarMonth />} onClick={() => setScheduleDialog(true)}>
              Schedule Playback Call
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteSweep />}
              onClick={handleClearHistory}
              disabled={!sessions.some(s => s.status === 'completed' || s.status === 'cancelled')}
            >
              Clear History
            </Button>
          </Stack>
        )}
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Manage playback sessions and review client feedback after assessment presentations.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <Tabs value={tabIndex} onChange={(_, v) => setTabIndex(v)} sx={{ mb: 2 }}>
        <Tab label={`Scheduled Sessions (${sessions.length})`} />
        <Tab label={`Client Feedback (${feedback.length})`} />
      </Tabs>

      {/* ── Scheduled Sessions Tab ── */}
      {tabIndex === 0 && (
        <>
          {sessions.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">No playback sessions scheduled yet.</Typography>
            </Paper>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Assessment</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Organization</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Start Time</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>End Time</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Participants</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Meeting Link</TableCell>
                    {isEMPortal && <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sessions.map((s) => (
                    <TableRow key={s.id} hover>
                      <TableCell>{s.assessment_domain || s.assessment_id?.slice(0, 8)}</TableCell>
                      <TableCell>{s.organization_name || '—'}</TableCell>
                      <TableCell>
                        {s.scheduled_date
                          ? `${displayInZone(s.scheduled_date, s.scheduled_timezone || 'UTC')} (${s.scheduled_timezone || 'UTC'})`
                          : '—'}
                      </TableCell>
                      <TableCell>
                        {s.scheduled_end_date
                          ? timeInZone(s.scheduled_end_date, s.scheduled_timezone || 'UTC')
                          : '—'}
                      </TableCell>
                      <TableCell>
                        <Chip label={s.status} size="small" color={getStatusColor(s.status)} />
                      </TableCell>
                      <TableCell>
                        {s.participants?.join(', ') || '—'}
                      </TableCell>
                      <TableCell>
                        {s.meeting_link ? (
                          <Button size="small" href={s.meeting_link} target="_blank" rel="noopener noreferrer">
                            Join
                          </Button>
                        ) : '—'}
                      </TableCell>
                      {isEMPortal && (
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Edit />}
                            onClick={() => openEditDialog(s)}
                            disabled={s.status === 'completed' || s.status === 'cancelled'}
                            sx={{ minWidth: 'auto', textTransform: 'none' }}
                          >
                            Edit
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<EventRepeat />}
                            color="warning"
                            onClick={() => handlePostponeSession(s)}
                            disabled={s.status === 'completed' || s.status === 'cancelled'}
                            sx={{ minWidth: 'auto', textTransform: 'none' }}
                          >
                            Postpone
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Cancel />}
                            color="error"
                            onClick={() => handleCancelSession(s)}
                            disabled={s.status === 'completed' || s.status === 'cancelled'}
                            sx={{ minWidth: 'auto', textTransform: 'none' }}
                          >
                            Cancel
                          </Button>
                          {(s.status === 'completed' || s.status === 'cancelled') && (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<Delete />}
                              color="inherit"
                              onClick={() => handleDeleteSession(s)}
                              sx={{ minWidth: 'auto', textTransform: 'none' }}
                            >
                              Delete
                            </Button>
                          )}
                        </Stack>
                      </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {/* ── Client Feedback Tab ── */}
      {tabIndex === 1 && (
        <>
          {feedback.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">No pending feedback to review.</Typography>
            </Paper>
          ) : (
            feedback.map((item) => (
              <Accordion key={item.id} sx={{ mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    <Typography sx={{ fontWeight: 500, flexGrow: 1 }}>
                      Feedback from session {item.playback_session_id?.slice(0, 8)}...
                    </Typography>
                    <Chip label={item.status} size="small" color={
                      item.status === 'incorporated' ? 'success' : 'warning'
                    } />
                    <Typography variant="caption" color="text.secondary">
                      {item.created_at ? new Date(item.created_at).toLocaleDateString() : ''}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Paper variant="outlined" sx={{ p: 2, mb: 2, backgroundColor: '#f5f5f5' }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {item.feedback_text || 'No text feedback provided.'}
                    </Typography>
                  </Paper>
                  {item.feedback_items && item.feedback_items.length > 0 && (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Section</TableCell>
                            <TableCell>Comment</TableCell>
                            <TableCell>Priority</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {item.feedback_items.map((fi: any, idx: number) => (
                            <TableRow key={idx}>
                              <TableCell>{fi.section}</TableCell>
                              <TableCell>{fi.comment}</TableCell>
                              <TableCell>
                                <Chip label={fi.priority || 'normal'} size="small" />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                  {item.status === 'submitted' && item.assessment_report_id && (
                    <Box sx={{ mt: 2, textAlign: 'right' }}>
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={<CheckCircle />}
                        onClick={() => handleIncorporate(item.assessment_report_id!)}
                      >
                        Mark as Incorporated
                      </Button>
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            ))
          )}
        </>
      )}

      {/* Edit Playback Session Dialog */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Playback Session</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Update the scheduled playback session details.
          </Typography>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Client Email"
              type="email"
              value={editClientEmail}
              onChange={(e) => setEditClientEmail(e.target.value)}
            />
            <TextField
              fullWidth
              label="Date"
              type="date"
              value={editDate}
              onChange={(e) => setEditDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              label="Start Time"
              type="time"
              value={editTime}
              onChange={(e) => setEditTime(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              label="End Time"
              type="time"
              value={editEndTime}
              onChange={(e) => setEditEndTime(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="Session auto-completes when end time passes"
            />
            <FormControl fullWidth>
              <InputLabel>Timezone</InputLabel>
              <Select
                value={editTimezone}
                onChange={(e) => setEditTimezone(e.target.value)}
                label="Timezone"
              >
                {COMMON_TIMEZONES.map((tz) => (
                  <MenuItem key={tz} value={tz}>{tz}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Teams Meeting Link"
              value={editMeetingLink}
              onChange={(e) => setEditMeetingLink(e.target.value)}
              placeholder="https://teams.microsoft.com/l/meetup-join/..."
            />
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={editStatus}
                onChange={(e) => setEditStatus(e.target.value)}
                label="Status"
              >
                <MenuItem value="scheduled">Scheduled</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Notes"
              value={editNotes}
              onChange={(e) => setEditNotes(e.target.value)}
              multiline
              rows={3}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveEdit}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Schedule Playback Call Dialog */}
      <Dialog open={scheduleDialog} onClose={closeScheduleDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Schedule Client Playback Call</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Schedule a playback call with the client to present assessment results.
            Create a Teams meeting first, then paste the join link below.
          </Typography>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {!isAssessmentLocked && (
              <>
                <FormControl fullWidth required>
                  <InputLabel id="playback-domain-label">Assessment Domain</InputLabel>
                  <Select
                    labelId="playback-domain-label"
                    value={selectedDomain}
                    onChange={(e) => {
                      setSelectedDomain(e.target.value);
                      setSelectedAssessmentType('');
                      setSelectedAssessmentId('');
                    }}
                    label="Assessment Domain"
                  >
                    {domainOptions.map((d) => (
                      <MenuItem key={d.name} value={d.name}>{d.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {selectedDomain && (
                  <FormControl fullWidth required>
                    <InputLabel id="playback-assessment-type-label">Assessment</InputLabel>
                    <Select
                      labelId="playback-assessment-type-label"
                      value={selectedAssessmentType}
                      onChange={(e) => {
                        setSelectedAssessmentType(e.target.value);
                        setSelectedAssessmentId('');
                      }}
                      label="Assessment"
                    >
                      {assessmentTypesForDomain.map((a) => (
                        <MenuItem key={a.name} value={a.name}>
                          {a.name} ({a.records.length} completed)
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                {selectedAssessmentType && recordsForAssessmentType.length === 0 && (
                  <Alert severity="info">
                    No completed client assessments for {selectedAssessmentType} in your assigned engagements yet.
                  </Alert>
                )}
                {recordsForAssessmentType.length > 0 && (
                  <FormControl fullWidth required>
                    <InputLabel id="playback-record-label">Client Assessment</InputLabel>
                    <Select
                      labelId="playback-record-label"
                      value={selectedAssessmentId}
                      onChange={(e) => setSelectedAssessmentId(e.target.value)}
                      label="Client Assessment"
                    >
                      {recordsForAssessmentType.map((r) => (
                        <MenuItem key={r.id} value={r.id}>{describeRecord(r)}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </>
            )}
            {isAssessmentLocked && (
              <Alert severity="info" sx={{ mt: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Selected Assessment (Locked):
                </Typography>
                <Typography variant="body2">
                  {(() => {
                    const selected = assessments.find(a => a.id === selectedAssessmentId);
                    return selected
                      ? `${selected.category} › ${selected.domain} — ${describeRecord(selected)}`
                      : 'Assessment selected';
                  })()}
                </Typography>
              </Alert>
            )}
            <TextField
              fullWidth
              required
              label="Client Email"
              type="email"
              value={clientEmail}
              onChange={(e) => setClientEmail(e.target.value)}
              placeholder="client@organization.com"
            />
            <TextField
              fullWidth
              required
              label="Date"
              type="date"
              value={scheduleDate}
              onChange={(e) => setScheduleDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              required
              label="Start Time"
              type="time"
              value={scheduleTime}
              onChange={(e) => setScheduleTime(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              label="End Time"
              type="time"
              value={scheduleEndTime}
              onChange={(e) => setScheduleEndTime(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="Defaults to 1 hour after start time if not specified. Session auto-completes when end time passes."
            />
            <FormControl fullWidth required>
              <InputLabel>Timezone</InputLabel>
              <Select
                value={scheduleTimezone}
                onChange={(e) => setScheduleTimezone(e.target.value)}
                label="Timezone"
              >
                {COMMON_TIMEZONES.map((tz) => (
                  <MenuItem key={tz} value={tz}>{tz}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              required
              label="Teams Meeting Link"
              value={meetingLink}
              onChange={(e) => setMeetingLink(e.target.value)}
              placeholder="https://teams.microsoft.com/l/meetup-join/..."
              helperText="Open Teams → Calendar → New Meeting → copy the join link and paste it here"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeScheduleDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSchedulePlayback}
            disabled={!scheduleDate || !scheduleTime || !clientEmail || !selectedAssessmentId || !meetingLink || scheduling}
          >
            {scheduling ? 'Scheduling...' : 'Schedule Session'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Preferences Dialog (timezone / time format) */}
      <UserPreferencesDialog
        open={prefsDialog}
        onClose={() => setPrefsDialog(false)}
        onSaved={(tz) => {
          setScheduleTimezone(tz);
          setEditTimezone(tz);
        }}
      />
    </Box>
  );
};

export default PlaybackFeedbackReview;
